import { Component, ElementRef, HostBinding, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { Subject, takeUntil } from 'rxjs';
import { QuickChatService } from 'app/layout/common/quick-chat/quick-chat.service';
import { Chat } from 'app/layout/common/quick-chat/quick-chat.types';
import { Router } from '@angular/router';
import { encrypt } from 'app/shared/utils';
import { FolderDoc } from 'app/modules/models/folderDoc.type';
import { StorageItemService } from 'app/shared/services/storageItem.service';
import { MatDialog } from '@angular/material/dialog';
import { FilePreviewDialogComponent } from 'app/modules/partners/file-preview/file-preview-dialog.component';
import { FullProjectFinancement } from 'app/modules/projetSica/projectFinancementBody';

@Component({
    selector     : 'quick-chat',
    templateUrl  : './quick-chat.component.html',
    styleUrls    : ['./quick-chat.component.scss'],
    encapsulation: ViewEncapsulation.None,
    exportAs     : 'quickChat'
})
export class QuickChatComponent implements OnInit, OnChanges, OnDestroy
{
    @ViewChild('messageInput') messageInput: ElementRef;
    chat: Chat;
    chats: Chat[];
    opened: boolean = false;
    selectedChat: Chat;
    @Input() folderChat: Chat;
    @Input() active : boolean;
    @Input() disabled: boolean = false;
    @Input() isfromProject: boolean;
    @Input() mainProjectFinancement: FullProjectFinancement;
    
    private _scrollStrategy: ScrollStrategy = this._scrollStrategyOptions.block();
    private _overlay: HTMLElement;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    dataType: string;
    isPdfFile: boolean = false;
    isImgFile: boolean = false;
    isOtherTypeFile: boolean = false;
    fileSource: any;

    /**
     * Constructor
     */
    constructor(
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _ngZone: NgZone,
        private router: Router,
        private _quickChatService: QuickChatService,
        private _scrollStrategyOptions: ScrollStrategyOptions,
        private _storageService: StorageItemService,
        private _dialog: MatDialog
    )
    {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(this.folderChat) {
            this.selectedChat = this.folderChat;
            this.chat = this.folderChat;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Decorated methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any
    {
        return {
            'quick-chat-opened': this.opened
        };
    }

    /**
     * Resize on 'input' and 'ngModelChange' events
     *
     * @public
     */
    @HostListener('input')
    @HostListener('ngModelChange')
    public _resizeMessageInput(): void
    {
        // This doesn't need to trigger Angular's change detection by itself
        this._ngZone.runOutsideAngular(() => {

            setTimeout(() => {

                // Set the height to 'auto' so we can correctly read the scrollHeight
                this.messageInput.nativeElement.style.height = 'auto';

                // Get the scrollHeight and subtract the vertical padding
                this.messageInput.nativeElement.style.height = `${this.messageInput.nativeElement.scrollHeight}px`;
            });
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Chat
        this._quickChatService.chat$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((chat: Chat) => {
                this.chat = chat;
            });

        // Chats
        this._quickChatService.chats$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((chats: Chat[]) => {
                this.chats = chats;
                this.selectChat(chats[0].id);
                this.opened = false;
            });

        // Selected chat
        this._quickChatService.chat$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((chat: Chat) => {
                this.selectedChat = chat;
            });
            this._hideOverlay();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the panel
     */
    open(): void
    {
        // Return if the panel has already opened
        if ( this.opened )
        {
            return;
        }

        // Open the panel
        this._toggleOpened(true);
    }

    /**
     * Close the panel
     */
    close(): void
    {
        // Return if the panel has already closed
        if ( !this.opened )
        {
            return;
        }

        // Close the panel
        this._toggleOpened(false);
    }

    /**
     * Toggle the panel
     */
    toggle(): void
    {
        // Si la messagerie est désactivée (ex : SAV en attente CPC), on ne fait rien
        if (this.disabled) {
            return;
        }
        if ( this.opened )
        {
            this.close();
        }
        else
        {
            this.open();
        }
    }

    /**
     * Select the chat
     *
     * @param id
     */
    selectChat(id: string): void
    {
        // Open the panel
        this._toggleOpened(true);

        // Get the chat data
        this._quickChatService.getChatById(id).subscribe();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Show the backdrop
     *
     * @private
     */
    private _showOverlay(): void
    {
        // Try hiding the overlay in case there is one already opened
        this._hideOverlay();

        // Create the backdrop element
        this._overlay = this._renderer2.createElement('div');

        // Return if overlay couldn't be create for some reason
        if ( !this._overlay )
        {
            return;
        }

        // Add a class to the backdrop element
        this._overlay.classList.add('quick-chat-overlay');

        // Append the backdrop to the parent of the panel
        this._renderer2.appendChild(this._elementRef.nativeElement.parentElement, this._overlay);

        // Enable block scroll strategy
        this._scrollStrategy.enable();

        // Add an event listener to the overlay
        this._overlay.addEventListener('click', () => {
            this.close();
        });
    }

    /**
     * Hide the backdrop
     *
     * @private
     */
    private _hideOverlay(): void
    {
        if ( !this._overlay )
        {
            return;
        }

        // If the backdrop still exists...
        if ( this._overlay )
        {
            // Remove the backdrop
            this._overlay.parentNode.removeChild(this._overlay);
            this._overlay = null;
        }

        // Disable block scroll strategy
        this._scrollStrategy.disable();
    }

    /**
     * Open/close the panel
     *
     * @param open
     * @private
     */
    private _toggleOpened(open: boolean): void
    {
        // Set the opened
        this.opened = open;

        // If the panel opens, show the overlay
        if ( open )
        {
            this._showOverlay();
        }
        // Otherwise, hide the overlay
        else
        {
            this._hideOverlay();
        }
    }

    openConversation(event: any): void {
        // redirect to the conversation page
        if(event) {
            if(this.isfromProject) {
            this.router.navigate(['/fiche-projet-avant-travaux/chats/' , this.mainProjectFinancement.projectFinancement.partnerId,  this.mainProjectFinancement.projectFinancement.projectTeraId]);
                return;
            }
            this.router.navigate(['/partners/chats/'+ encrypt(this.folderChat?.type+'_'+this.folderChat?.folderId)]);
        }
    }

    previewFile(file: FolderDoc, fileType): void {
        event.preventDefault(); // Prevent the default link behavior
        const extensionIndex = file.fileName.lastIndexOf('.');

        if (extensionIndex !== -1) {  // Check if there is an extension
            const fileExtension = file.fileName.substr(extensionIndex);  // Get the extension (including dot)
            file.fileName = file.fileName.substring(0, extensionIndex) + fileExtension.toLowerCase(); // Replace with lowercase extension
        }
        
        file.fileName = file.nomFichier ?? file.fileName;
        this._storageService.downloadFolderDoc(file).subscribe((response: any) => {
          this.dataType = String(file.fileName.substr(-3));
          this.getFile(response)
          const dialogRef = this._dialog.open(FilePreviewDialogComponent, {
            width: '80%',
            // disableClose: true,
            data: {
              type: 'document',
              fileName: file.fileName,
              isEditDisabled: true,
              version: this.fileSource,
              isPdfFile: this.isPdfFile,
              isImgFile: this.isImgFile,
              isOtherTypeFile: this.isOtherTypeFile,
              file: file
            },
          });
    
          dialogRef.afterClosed().subscribe(result => { });
        });
    }

    getFile(response) {
        var imageExtension = ['png', 'jpg', 'jpeg', 'gif','webp']; 
        this.isPdfFile = false;
        this.isImgFile = false;
        this.isOtherTypeFile = false;
        if(this.dataType == 'pdf'){
          this.isPdfFile = true;
          this.fileSource = 'data:application/pdf;base64,' + response;
        } else if (imageExtension.includes(this.dataType)) {
          const imageType = 'image/'+ this.dataType;
          const base64Image = `data:${imageType};base64,${response}`;
          this.fileSource = base64Image;
            this.isImgFile = true;
        }else {
            this.isOtherTypeFile = true;
        }
    }

    isImageFile(fileName: string): boolean {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
        return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
      }
}
